// Right-hand panel: what can be done to the selected section. Text and photos
// are edited on the page itself; this panel is for everything else.
import React, { useState } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { ArrowDown, ArrowUp, Code2, Eye, EyeOff, ImageIcon, Loader2, RefreshCw, Sparkles, Trash2, X } from "lucide-react";
import { useSiteSectionEditMutation, useSiteSectionMutation } from "../../store/Reducer/aiApi";
import { ConfirmDialog } from "../admin-dashboard/ui";
import { fetchPreviewHtml } from "./SitePreview";
import { htmlOf, imagesOf, LIVE_PAGES, outlineOf, partName, patchImage, sectionsOf, setHtml, updateSection, withSections } from "./siteModel";

const QUICK_EDITS = ["Make it more eye-catching", "Shorter, simpler text", "Try a different layout", "Add a photo", "Use more of the accent colour"];

const aiError = (err, fallback) => err?.data?.error || err?.error || fallback;

const HtmlDialog = ({ site, id, onApply, onClose }) => {
  const token = useSelector((state) => state.Auth?.user?.accessToken);
  const [html, setValue] = useState(htmlOf(site, id));
  const [error, setError] = useState(null);
  const [checking, setChecking] = useState(false);
  const apply = async () => {
    setChecking(true);
    setError(null);
    const next = setHtml(site, id, html);
    try {
      // The preview runs the same safety check as saving.
      await fetchPreviewHtml(token, next, false);
      onApply(next);
    } catch (err) {
      setError(err.message);
    } finally {
      setChecking(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label="Edit HTML" className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="font-semibold text-slate-800">Edit HTML: {partName(site, id)}</h3>
          <button type="button" aria-label="Close" onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100"><X size={18} /></button>
        </div>
        <p className="px-5 pt-3 text-xs text-gray-500">
          Tailwind classes and Liquid tags such as {"{{ masjid.name }}"} work. Scripts, styles and other unsafe code are removed when you save.
        </p>
        <textarea aria-label="HTML" spellCheck={false} value={html} onChange={(e) => setValue(e.target.value)}
          className="m-5 min-h-[50vh] flex-1 rounded-lg border border-gray-300 p-3 font-mono text-xs focus:border-accent focus:outline-none" />
        {error && <p role="alert" className="mx-5 -mt-2 mb-3 rounded bg-red-50 p-2 text-sm text-red-700">{error}</p>}
        <div className="flex justify-end gap-2 rounded-b-xl bg-gray-50 px-5 py-3">
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
          <button type="button" onClick={apply} disabled={checking} className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
            {checking ? "Checking…" : "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
};

// The section's photos: replace with your own, or remove. Live photos (events,
// news, the logo) are changed where that data is edited.
const PhotoList = ({ html, onReplace, onRemove, onLive }) => {
  const { fixed, live } = imagesOf(html);
  if (!fixed.length && !live.length) return null;
  return (
    <div>
      <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-800"><ImageIcon size={15} /> Photos</h3>
      {fixed.length > 0 && (
        <ul className="grid grid-cols-2 gap-2">
          {fixed.map((p) => (
            <li key={p.id} className="overflow-hidden rounded-lg border border-gray-200">
              {p.src ? <img src={p.src} alt={p.alt} className="aspect-[4/3] w-full object-cover" /> : <div className="flex aspect-[4/3] items-center justify-center bg-gray-100 text-xs text-gray-400">No photo</div>}
              <div className="flex">
                <button type="button" onClick={() => onReplace(p.id)} className="flex-1 py-1.5 text-xs font-semibold text-accent hover:bg-accent/5">Replace</button>
                <button type="button" onClick={() => onRemove(p.id)} aria-label="Remove photo" className="border-l px-2 text-gray-400 hover:bg-red-50 hover:text-red-600"><Trash2 size={13} /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {live.map((source) => (
        <p key={source} className="mt-2 text-xs text-gray-500">
          Some photos here come from your {LIVE_PAGES[source]?.[0] || source}.{" "}
          <button type="button" onClick={() => onLive(source)} className="font-medium text-accent hover:underline">Change them there</button>
        </p>
      ))}
    </div>
  );
};

const SectionPanel = ({ site, id, onChange, onClose, onBusy, onReplaceImage, onLive }) => {
  const [instruction, setInstruction] = useState("");
  const [working, setWorking] = useState(null); // "edit" | "regenerate"
  const [htmlOpen, setHtmlOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editSection] = useSiteSectionEditMutation();
  const [buildSection] = useSiteSectionMutation();

  if (!id) {
    return (
      <div className="p-5 text-sm text-gray-500">
        <p className="font-medium text-slate-700">Select a section</p>
        <p className="mt-1">Click a section on the page or in the list to move it, hide it, or change it with AI.</p>
      </div>
    );
  }

  const sections = sectionsOf(site);
  const index = sections.findIndex((s) => s.id === id);
  const section = sections[index];
  const isPart = id === "header" || id === "footer";
  const name = partName(site, id);

  const run = async (kind, task) => {
    setWorking(kind);
    onBusy?.(true);
    try {
      await task();
    } finally {
      setWorking(null);
      onBusy?.(false);
    }
  };

  const aiEdit = (text) => run("edit", async () => {
    const ask = (text || instruction).trim();
    if (ask.length < 3) return;
    try {
      const res = await editSection({ outline: outlineOf(site), sectionId: id, name, html: htmlOf(site, id), instruction: ask }).unwrap();
      onChange((current) => setHtml(current, id, res.html));
      setInstruction("");
      toast.success(`${name} updated`);
    } catch (err) {
      toast.error(aiError(err, "The AI couldn't make that change. Try again or say it differently."));
    }
  });

  const regenerate = () => run("regenerate", async () => {
    try {
      const res = await buildSection({ outline: outlineOf(site), sectionId: id, name, brief: section?.prompt || name }).unwrap();
      onChange((current) => setHtml(current, id, res.html));
      toast.success(`New design for ${name}`);
    } catch (err) {
      toast.error(aiError(err, "The AI couldn't redesign this section. Try again."));
    }
  });

  const move = (delta) => {
    const next = [...sections];
    const [item] = next.splice(index, 1);
    next.splice(index + delta, 0, item);
    onChange(withSections(site, next));
  };

  return (
    <div className="flex flex-col gap-5 p-4">
      <div className="flex items-start justify-between gap-2">
        {isPart ? (
          <h2 className="text-base font-semibold text-slate-800">{name}</h2>
        ) : (
          <input aria-label="Section name" value={section?.name || ""} maxLength={60}
            onChange={(e) => onChange(updateSection(site, id, { name: e.target.value }), `name-${id}`)}
            className="-ml-2 min-w-0 flex-1 rounded-md border border-transparent px-2 py-1 text-base font-semibold text-slate-800 hover:border-gray-200 focus:border-accent focus:outline-none" />
        )}
        <button type="button" aria-label="Close" onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100"><X size={18} /></button>
      </div>

      <div className="rounded-xl border border-violet-200 bg-violet-50/60 p-3">
        <label htmlFor="ai-edit" className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-violet-900"><Sparkles size={15} /> Edit with AI</label>
        <textarea id="ai-edit" rows={3} value={instruction} maxLength={1000} disabled={!!working}
          onChange={(e) => setInstruction(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) aiEdit(); }}
          placeholder={`e.g. "Make the background dark green and add a donate button"`}
          className="w-full rounded-lg border border-violet-200 bg-white px-3 py-2 text-sm focus:border-violet-400 focus:outline-none" />
        <div className="mt-2 flex flex-wrap gap-1.5">
          {QUICK_EDITS.map((q) => (
            <button key={q} type="button" disabled={!!working} onClick={() => aiEdit(q)}
              className="rounded-full border border-violet-200 bg-white px-2.5 py-1 text-xs text-violet-800 hover:bg-violet-100 disabled:opacity-50">{q}</button>
          ))}
        </div>
        <button type="button" onClick={() => aiEdit()} disabled={!!working || instruction.trim().length < 3}
          className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-violet-600 px-3 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50">
          {working === "edit" ? <><Loader2 size={15} className="animate-spin" /> Working on it…</> : "Apply"}
        </button>
      </div>

      <PhotoList
        html={htmlOf(site, id)}
        onReplace={(imgId) => onReplaceImage?.(imgId)}
        onRemove={(imgId) => onChange((current) => setHtml(current, id, patchImage(htmlOf(current, id), imgId, "")))}
        onLive={(source) => onLive?.(source)}
      />

      <div className="grid grid-cols-2 gap-2 text-sm">
        <button type="button" onClick={regenerate} disabled={!!working}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 font-medium text-slate-700 hover:bg-gray-50 disabled:opacity-50">
          {working === "regenerate" ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} New design
        </button>
        <button type="button" onClick={() => setHtmlOpen(true)} disabled={!!working}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 font-medium text-slate-700 hover:bg-gray-50 disabled:opacity-50">
          <Code2 size={15} /> Edit HTML
        </button>
        {!isPart && (
          <>
            <button type="button" onClick={() => move(-1)} disabled={index <= 0}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 font-medium text-slate-700 hover:bg-gray-50 disabled:opacity-40">
              <ArrowUp size={15} /> Move up
            </button>
            <button type="button" onClick={() => move(1)} disabled={index >= sections.length - 1}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 font-medium text-slate-700 hover:bg-gray-50 disabled:opacity-40">
              <ArrowDown size={15} /> Move down
            </button>
            <button type="button" onClick={() => onChange(updateSection(site, id, { hidden: !section.hidden }))}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 font-medium text-slate-700 hover:bg-gray-50">
              {section?.hidden ? <><Eye size={15} /> Show</> : <><EyeOff size={15} /> Hide</>}
            </button>
            <button type="button" onClick={() => setConfirmDelete(true)}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-red-200 px-3 py-2 font-medium text-red-700 hover:bg-red-50">
              <Trash2 size={15} /> Delete
            </button>
          </>
        )}
      </div>

      {htmlOpen && <HtmlDialog site={site} id={id} onApply={(next) => { onChange(next); setHtmlOpen(false); }} onClose={() => setHtmlOpen(false)} />}
      <ConfirmDialog
        isOpen={confirmDelete}
        title={`Delete ${name}?`}
        description="You can undo this until you leave the editor."
        confirmLabel="Delete section"
        danger
        onConfirm={() => { setConfirmDelete(false); onChange(withSections(site, sections.filter((s) => s.id !== id))); onClose(); }}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
};

export default SectionPanel;
